import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Icon } from "../Icon";

const TONES = {
  success: { icon: "Check", iconBg: "var(--kr-success-500)" },
  error: { icon: "X", iconBg: "var(--kr-danger-500)" },
  warning: { icon: "AlertTriangle", iconBg: "var(--kr-warning-500)" },
  info: { icon: "Info", iconBg: "var(--kr-action)" },
};

const DEFAULT_DURATION = { success: 4000, info: 4000, warning: 5000, error: 6000 };

const ToastContext = createContext(null);

let nextId = 1;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timeoutsRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((arr) => arr.filter((t) => t.id !== id));
    const t = timeoutsRef.current.get(id);
    if (t) {
      clearTimeout(t);
      timeoutsRef.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (tone, title, description, options = {}) => {
      const id = nextId++;
      const duration =
        options.duration ?? DEFAULT_DURATION[tone] ?? DEFAULT_DURATION.info;
      setToasts((arr) => [...arr, { id, tone, title, description }]);
      if (duration > 0) {
        const handle = setTimeout(() => {
          setToasts((arr) => arr.filter((t) => t.id !== id));
          timeoutsRef.current.delete(id);
        }, duration);
        timeoutsRef.current.set(id, handle);
      }
      return id;
    },
    [],
  );

  useEffect(() => () => {
    timeoutsRef.current.forEach((h) => clearTimeout(h));
    timeoutsRef.current.clear();
  }, []);

  const api = {
    success: (title, description, options) =>
      push("success", title, description, options),
    error: (title, description, options) =>
      push("error", title, description, options),
    info: (title, description, options) =>
      push("info", title, description, options),
    warning: (title, description, options) =>
      push("warning", title, description, options),
    dismiss,
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used inside <ToastProvider>");
  }
  return ctx;
}

function ToastViewport({ toasts, onDismiss }) {
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      aria-live="polite"
      aria-atomic="true"
      style={{
        position: "fixed",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
        zIndex: 1100,
        pointerEvents: "none",
        maxWidth: "min(440px, calc(100vw - 32px))",
      }}
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>,
    document.body,
  );
}

function ToastItem({ toast, onDismiss }) {
  const t = TONES[toast.tone] || TONES.info;
  return (
    <div
      role="status"
      onClick={onDismiss}
      style={{
        pointerEvents: "auto",
        display: "flex",
        alignItems: "center",
        gap: 14,
        background: "var(--kr-fg-strong)",
        color: "#fff",
        borderRadius: 10,
        boxShadow: "var(--kr-shadow-lg)",
        padding: "14px 22px",
        cursor: "pointer",
        animation: "kr-toast-in var(--kr-dur-base) var(--kr-ease-out)",
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 30,
          height: 30,
          borderRadius: 999,
          background: t.iconBg,
          color: "#fff",
          flexShrink: 0,
        }}
      >
        <Icon name={t.icon} size={16} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        {toast.title && (
          <div style={{ fontSize: 14, fontWeight: 600 }}>{toast.title}</div>
        )}
        {toast.description && (
          <div
            style={{
              fontSize: 12,
              opacity: 0.7,
              fontFamily: "var(--kr-font-mono)",
              marginTop: toast.title ? 3 : 0,
            }}
          >
            {toast.description}
          </div>
        )}
      </div>
    </div>
  );
}

if (typeof document !== "undefined" && !document.getElementById("kr-toast-keyframes")) {
  const s = document.createElement("style");
  s.id = "kr-toast-keyframes";
  s.textContent = `
    @keyframes kr-toast-in {
      from { opacity: 0; transform: translateY(8px) scale(0.98); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }
  `;
  document.head.appendChild(s);
}

export default ToastProvider;
